import type { CSSProperties, ReactElement } from 'react';
import {
  forwardRef,
  Fragment,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useMemoizedFn } from 'ahooks';
import { Button, Result, Spin } from 'antd';
import { isDeepEqual } from '@dimjs/lang';
import { classNames } from '@dimjs/utils';
import type { TAny, TPlainObject } from '@flatbiz/utils';
import { getValueOrDefault, isUndefinedOrNull, toArray } from '@flatbiz/utils';
import { fbaHooks } from '../fba-hooks/index.js';
import { LocalLoadingCtxProvider } from './context.js';
import './style.less';

export type LocalLoadingServiceConfig = {
  onRequest: (params?: TAny) => Promise<TAny>;
  params?: TPlainObject;
  /** 标记serviceConfig.params中无效参数，被设置的params key 不传入服务接口入参  */
  invalidParamKey?: string[];
  /** 必填字段设置 */
  requiredParamsKeys?: string[];
};

export interface LocalLoadingProps {
  className?: string;
  style?: CSSProperties;
  contentStyle?: CSSProperties;
  /** 接口数据配置 */
  serviceConfig: LocalLoadingServiceConfig;
  /** children 为函数，参数【respData】为接口返回数据 */
  children: (respData?: TAny) => ReactElement;
  /**
   * 是否异步，默认：false
   * ```
   * true（异步）：onRequest、react dom渲染同步执行
   * false（同步）：onRequest有结果了才渲染 react dom
   * ```
   */
  isAsync?: boolean;
  /** loading高度，默认值：100；isAsync = true 无效 */
  loadingHeight?: number | string;
  /** loading spin 属性设置 */
  loadingSpinProps?: {
    /** loading tip，默认值：Loading */
    tip?: string;
    /** loading 内容 */
    content?: ReactElement;
    /** 背景色，默认值：rgba(0,0,0,0.01) */
    bgColor?: string;
  };
  textConfigs?: {
    /** 必填异常文本 */
    requiredErrorText?: string;
  };
  /** 是否显示必填异常 */
  showRequiredError?: boolean;
  /** 自定义异常渲染处理 */
  errorRender?: (error?: TAny) => ReactElement;
  /** 设置内部滚动，需要设置 style.height 才能生效，@5.0.16 */
  contentScroll?: boolean;
  /** 设置高度 */
  height?: number | string;
}

export type LocalLoadingRefApi = {
  onRefresh: (params?: TPlainObject) => void;
};

/**
 * 局部加载，包含接口数据处理逻辑
 * ```
 * 包括
 * 1. loading显示效果、error显示效果、获取服务数据
 * 2. 当 serviceConfig.params 值与上一次值不相等时，会主动发起服务调用
 * 3. 通过 serviceConfig.requiredParamsKeys 设置服务调用必填字段
 * 4. 通过 ref.onRequest 可主动发起服务调用
 * 5. 子组件通过下面方式主动发起服务调用
 *    const localLoadingApi = LocalLoading.useLocalLoading();
 *    localLoadingApi.onRequest();
 * 6. 如果内部需要滚动条，可设置 contentScroll = true （外部有高度或者设置style.height 才能生效）
 * ```
 */
export const LocalLoadingInner = forwardRef<
  LocalLoadingRefApi,
  LocalLoadingProps
>((props, ref) => {
  const {
    serviceConfig,
    isAsync,
    children,
    errorRender,
    loadingSpinProps,
    textConfigs,
    showRequiredError,
    contentScroll,
    contentStyle,
    height,
  } = props;
  const [status, setStatus] = useState<
    'success' | 'error' | 'required_error' | 'init'
  >('init');
  const [respData, setRespData] = useState<TAny>();
  const loadingHeight =
    props.loadingHeight === undefined ? 100 : props.loadingHeight;
  const [loading, setLoading] = useState<boolean>(false);
  const errorRef = useRef<string>();

  const prevParams = fbaHooks.usePrevious(serviceConfig.params);

  const loadingSpinContent = getValueOrDefault(
    loadingSpinProps?.content,
    <span />
  );
  const loadingSpinBgColor = getValueOrDefault(
    loadingSpinProps?.bgColor,
    'rgba(0,0,0,0.01)'
  );
  const loadingSpinTip = getValueOrDefault(loadingSpinProps?.tip, 'Loading');

  const requiredErrorText = textConfigs?.requiredErrorText;

  // 用于直接发起接口调用，不能用于比较
  const serviceParams = useMemo(() => {
    if (
      !serviceConfig.params ||
      toArray(serviceConfig.invalidParamKey).length === 0
    ) {
      return serviceConfig.params;
    }
    const newParams = { ...serviceConfig.params };
    serviceConfig.invalidParamKey?.forEach((key) => {
      newParams[key] = undefined;
    });
    return newParams;
  }, [serviceConfig]);

  // 判断必要参数是否完整
  const validateRequiredParamsComplete = useMemoizedFn(
    (params?: TPlainObject) => {
      const keys = serviceConfig?.requiredParamsKeys;
      if (keys?.length) {
        const target = keys.find((item) => {
          return isUndefinedOrNull(params?.[item]);
        });
        if (target) {
          return {
            complete: false,
            message:
              requiredErrorText ||
              `LocalLoading渲染缺少必填参数：${keys.join('、')}`,
          };
        }
      }
      return { complete: true };
    }
  );

  const completeResult = useMemo(() => {
    return validateRequiredParamsComplete(serviceConfig.params);
  }, [validateRequiredParamsComplete, serviceConfig.params]);

  const onInitRequest = async (params?: TPlainObject) => {
    try {
      const mergeProps = { ...serviceParams, ...params };
      setLoading(true);
      const respData = await serviceConfig.onRequest(mergeProps);
      setStatus('success');
      setRespData(respData);
    } catch (error: TAny) {
      console.error(error);
      setStatus('error');
      errorRef.current = error.message;
    } finally {
      setLoading(false);
    }
  };

  fbaHooks.useEffectCustomAsync(async () => {
    if (completeResult.complete) {
      await onInitRequest();
    }
  }, []);

  fbaHooks.useEffectCustom(() => {
    if (completeResult.complete) {
      if (prevParams) {
        if (!isDeepEqual(serviceConfig.params, prevParams)) {
          if (status === 'error') {
            setStatus('init');
          }
          void onInitRequest();
        }
      }
    }
  }, [prevParams, serviceConfig.params]);

  useImperativeHandle(ref, () => {
    return { onRefresh: onInitRequest };
  });

  if (!completeResult.complete) {
    if (!showRequiredError) {
      return <Fragment />;
    }
    return (
      <LocalLoadingCtxProvider value={{ onRequest: onInitRequest }}>
        <div className={classNames('local-loading-error', props.className)}>
          <div className="local-loading-area"></div>
          <Result status="error" subTitle={completeResult.message} />
        </div>
      </LocalLoadingCtxProvider>
    );
  }

  if (status === 'error') {
    if (errorRender) {
      return errorRender({ message: errorRef.current });
    }
    return (
      <LocalLoadingCtxProvider value={{ onRequest: onInitRequest }}>
        <div className={classNames('local-loading-error', props.className)}>
          <div className="local-loading-area"></div>
          <Result
            status="error"
            subTitle={errorRef.current || '数据处理异常'}
            extra={[
              <Button
                type="primary"
                ghost
                key="console"
                size="small"
                onClick={() => {
                  setStatus('init');
                  void onInitRequest();
                }}
              >
                重新获取
              </Button>,
            ]}
          />
        </div>
      </LocalLoadingCtxProvider>
    );
  }

  if (status !== 'success' && !isAsync) {
    return (
      <div
        className={classNames('fba-local-loading-process', props.className)}
        style={{
          height: loadingHeight,
          display: 'flex',
          justifyContent: 'center',
          position: 'relative',
          backgroundColor: 'var(--block-bg-color)',
        }}
      >
        <Spin
          spinning={loading}
          tip={loadingSpinTip}
          wrapperClassName="local-loading-spin-wrapper"
          className="local-loading-spin"
          style={
            {
              '--local-loading-spin-bgColor': loadingSpinBgColor,
            } as CSSProperties
          }
        >
          {loadingSpinContent}
        </Spin>
      </div>
    );
  }

  const className = classNames(
    'fba-local-loading',
    { 'fll-content-scroll': contentScroll },
    props.className
  );

  const rootStyle = { height, ...props.style };

  return (
    <LocalLoadingCtxProvider value={{ onRequest: onInitRequest }}>
      <div className={className} style={rootStyle}>
        {loading ? (
          <Spin
            spinning={loading}
            tip={loadingSpinTip}
            wrapperClassName="local-loading-spin-wrapper"
            className="local-loading-spin"
            style={
              {
                '--local-loading-spin-bgColor': loadingSpinBgColor,
              } as CSSProperties
            }
          >
            {loadingSpinContent}
          </Spin>
        ) : null}
        <div className="local-loading-content" style={contentStyle}>
          {children(respData)}
        </div>
      </div>
    </LocalLoadingCtxProvider>
  );
});
